/**
 * Fantasy Console 0 - Error Panel Component
 *
 * Displays runtime errors from the console with source context.
 */

import { ErrorPayload } from '@fc0/types'
import { ErrorIcon } from '../shared/icons'
import styles from './ErrorPanel.module.css'

interface RuntimeError extends ErrorPayload {
  source?: 'syntax' | 'runtime' | 'init' | 'update' | 'draw'
  timestamp?: number
}

interface ErrorPanelProps {
  errors: RuntimeError[]
  onClear: () => void
  onJumpToLine?: (line: number) => void
}

export function ErrorPanel({ errors, onClear, onJumpToLine }: ErrorPanelProps) {
  if (errors.length === 0) {
    return null
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <ErrorIcon />
          <span className={styles.count}>
            {errors.length} error{errors.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          className={styles.clearButton}
          onClick={onClear}
          title='Clear all errors'
        >
          <ClearIcon />
          <span>Clear</span>
        </button>
      </div>

      <div className={styles.list}>
        {errors.map((error, index) => (
          <ErrorItem key={index} error={error} onJumpToLine={onJumpToLine} />
        ))}
      </div>
    </div>
  )
}

interface ErrorItemProps {
  error: RuntimeError
  onJumpToLine?: (line: number) => void
}

function ErrorItem({ error, onJumpToLine }: ErrorItemProps) {
  const hasLineNumber = error.line !== undefined && error.line > 0

  const handleClick = () => {
    if (hasLineNumber && onJumpToLine) {
      onJumpToLine(error.line!)
    }
  }

  return (
    <div className={styles.item}>
      <div className={styles.itemHeader}>
        {error.source && <span className={styles.source}>{error.source}</span>}
        {hasLineNumber && (
          <button
            className={styles.lineNumber}
            onClick={handleClick}
            title={`Jump to line ${error.line}`}
          >
            line {error.line}
          </button>
        )}
      </div>
      <div className={styles.message}>{error.message}</div>
      {error.stack && (
        <details className={styles.stackDetails}>
          <summary className={styles.stackSummary}>Stack trace</summary>
          <pre className={styles.stack}>{error.stack}</pre>
        </details>
      )}
    </div>
  )
}

function ClearIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M3 3l8 8m0-8L3 11' />
    </svg>
  )
}

export type { RuntimeError }
